import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import {
  Loader2,
  RefreshCw,
  Shield,
  ShieldOff,
  AlertCircle,
  CheckCircle,
  UserPlus,
  Search,
  UserCheck,
  UserX,
  Users,
  Crown,
  Star,
  Gift,
  ChevronDown
} from 'lucide-react';

const TIER_CONFIG = {
  free: { label: 'Free', icon: Gift, color: 'text-slate-600', bg: 'bg-slate-100' },
  pro: { label: 'Pro', icon: Star, color: 'text-blue-600', bg: 'bg-blue-100' },
  enterprise: { label: 'Enterprise', icon: Crown, color: 'text-purple-600', bg: 'bg-purple-100' }
};

function UsersTab() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminId, setNewAdminId] = useState('');
  const [filter, setFilter] = useState('all'); // all, approved, restricted, admins

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    // Fetch all users (not just admins)
    const response = await amplifyApi.adminGetAllUsers();
    if (response.success) {
      setUsers(response.users || []);
    } else {
      setError(response.error || 'Failed to load users');
    }

    setLoading(false);
  };

  const handleApproveUser = async (userId) => {
    setActionLoading(`approve-${userId}`);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.adminApproveUser(userId);
    if (response.success) {
      setSuccess(`User approved - their public products are now visible`);
      loadUsers();
    } else {
      setError(response.error || 'Failed to approve user');
    }

    setActionLoading(null);
  };

  const handleRestrictUser = async (userId) => {
    setActionLoading(`restrict-${userId}`);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.adminRestrictUser(userId);
    if (response.success) {
      setSuccess(`User restricted - their public products are now hidden`);
      loadUsers();
    } else {
      setError(response.error || 'Failed to restrict user');
    }

    setActionLoading(null);
  };

  const handleGrantAdmin = async (userId) => {
    setActionLoading(`admin-${userId}`);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.grantAdminAccess(userId);
    if (response.success) {
      setSuccess(`Admin access granted`);
      loadUsers();
    } else {
      setError(response.error || 'Failed to grant admin access');
    }

    setActionLoading(null);
  };

  const handleRevokeAdmin = async (userId) => {
    if (!window.confirm(`Are you sure you want to revoke admin access?`)) {
      return;
    }

    setActionLoading(`admin-${userId}`);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.revokeAdminAccess(userId);
    if (response.success) {
      setSuccess(`Admin access revoked`);
      loadUsers();
    } else {
      setError(response.error || 'Failed to revoke admin access');
    }

    setActionLoading(null);
  };

  const handleAddNewAdmin = async () => {
    if (!newAdminId.trim()) {
      setError('Please enter a user ID');
      return;
    }

    setActionLoading('new');
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.grantAdminAccess(newAdminId.trim());
    if (response.success) {
      setSuccess(`Admin access granted to ${newAdminId}`);
      setNewAdminId('');
      setShowAddAdmin(false);
      loadUsers();
    } else {
      setError(response.error || 'Failed to grant admin access');
    }

    setActionLoading(null);
  };

  const handleTierChange = async (userId, newTier) => {
    setActionLoading(`tier-${userId}`);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.updateUserTier(userId, newTier);
    if (response.success) {
      setSuccess(`User tier updated to ${newTier}`);
      loadUsers();
    } else {
      setError(response.error || 'Failed to update user tier');
    }

    setActionLoading(null);
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    // Search filter
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query ||
      user.userId?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query);

    if (!matchesSearch) return false;

    // Status filter
    switch (filter) {
      case 'approved':
        return user.isApproved === true;
      case 'restricted':
        return user.isApproved !== true;
      case 'admins':
        return user.isAdmin === true;
      default:
        return true;
    }
  });

  // Sort: admins first, then approved, then restricted
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (a.isAdmin && !b.isAdmin) return -1;
    if (!a.isAdmin && b.isAdmin) return 1;
    if (a.isApproved && !b.isApproved) return -1;
    if (!a.isApproved && b.isApproved) return 1;
    return 0;
  });

  // Stats
  const stats = {
    total: users.length,
    approved: users.filter(u => u.isApproved).length,
    restricted: users.filter(u => !u.isApproved).length,
    admins: users.filter(u => u.isAdmin).length
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">User Management</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage user approval status and admin access
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAddAdmin(!showAddAdmin)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <UserPlus className="w-4 h-4" />
            Add Admin
          </button>
          <button
            onClick={loadUsers}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-xs">Total Users</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <UserCheck className="w-4 h-4" />
            <span className="text-xs">Approved</span>
          </div>
          <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-amber-600 mb-1">
            <UserX className="w-4 h-4" />
            <span className="text-xs">Restricted</span>
          </div>
          <p className="text-2xl font-bold text-amber-700">{stats.restricted}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <Shield className="w-4 h-4" />
            <span className="text-xs">Admins</span>
          </div>
          <p className="text-2xl font-bold text-purple-700">{stats.admins}</p>
        </div>
      </div>

      {/* Add Admin Form */}
      {showAddAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-3">Add New Admin</h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={newAdminId}
              onChange={(e) => setNewAdminId(e.target.value)}
              placeholder="Enter Cognito User ID (sub)"
              className="flex-1 px-3 py-2 border border-blue-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleAddNewAdmin}
              disabled={actionLoading === 'new'}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
            >
              {actionLoading === 'new' && <Loader2 className="w-4 h-4 animate-spin" />}
              Grant Access
            </button>
            <button
              onClick={() => {
                setShowAddAdmin(false);
                setNewAdminId('');
              }}
              className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            &times;
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-500 hover:text-green-700">
            &times;
          </button>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email or user ID..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'approved', 'restricted', 'admins'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                filter === f
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600">User</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Tier</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Earnings</th>
              <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedUsers.map((user) => (
              <tr key={user.userId} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div>
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {user.email || 'Unknown'}
                      {user.isAdmin && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded">
                          ADMIN
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 font-mono">
                      {user.userId?.substring(0, 24)}...
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  {user.isApproved ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      <UserCheck className="w-3 h-3" />
                      Approved
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                      <UserX className="w-3 h-3" />
                      Restricted
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {/* Tier dropdown */}
                  <div className="relative">
                    {actionLoading === `tier-${user.userId}` ? (
                      <div className="flex items-center gap-2 px-2 py-1">
                        <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                        <span className="text-xs text-slate-400">Updating...</span>
                      </div>
                    ) : (
                      <select
                        value={user.tier || 'free'}
                        onChange={(e) => handleTierChange(user.userId, e.target.value)}
                        className={`appearance-none pl-7 pr-6 py-1 text-xs font-medium rounded-full border cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          TIER_CONFIG[user.tier || 'free'].bg
                        } ${TIER_CONFIG[user.tier || 'free'].color} border-transparent`}
                      >
                        <option value="free">Free</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    )}
                    {actionLoading !== `tier-${user.userId}` && (
                      <>
                        {React.createElement(TIER_CONFIG[user.tier || 'free'].icon, {
                          className: `w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 ${TIER_CONFIG[user.tier || 'free'].color} pointer-events-none`
                        })}
                        <ChevronDown className={`w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 ${TIER_CONFIG[user.tier || 'free'].color} pointer-events-none`} />
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div className="text-xs">
                    <div>Total: ${((user.totalEarnings || 0) / 100).toFixed(2)}</div>
                    <div className="text-slate-400">Pending: ${((user.pendingBalance || 0) / 100).toFixed(2)}</div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {/* Approval toggle */}
                    {user.isApproved ? (
                      <button
                        onClick={() => handleRestrictUser(user.userId)}
                        disabled={actionLoading === `restrict-${user.userId}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-amber-600 hover:bg-amber-50 border border-amber-200 rounded-lg transition-colors disabled:opacity-50"
                        title="Restrict user - hide their public products"
                      >
                        {actionLoading === `restrict-${user.userId}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <UserX className="w-3 h-3" />
                        )}
                        Restrict
                      </button>
                    ) : (
                      <button
                        onClick={() => handleApproveUser(user.userId)}
                        disabled={actionLoading === `approve-${user.userId}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-green-600 hover:bg-green-50 border border-green-200 rounded-lg transition-colors disabled:opacity-50"
                        title="Approve user - show their public products"
                      >
                        {actionLoading === `approve-${user.userId}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <UserCheck className="w-3 h-3" />
                        )}
                        Approve
                      </button>
                    )}

                    {/* Admin toggle */}
                    {user.isAdmin ? (
                      <button
                        onClick={() => handleRevokeAdmin(user.userId)}
                        disabled={actionLoading === `admin-${user.userId}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
                        title="Revoke admin access"
                      >
                        {actionLoading === `admin-${user.userId}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <ShieldOff className="w-3 h-3" />
                        )}
                        Revoke Admin
                      </button>
                    ) : (
                      <button
                        onClick={() => handleGrantAdmin(user.userId)}
                        disabled={actionLoading === `admin-${user.userId}`}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs text-purple-600 hover:bg-purple-50 border border-purple-200 rounded-lg transition-colors disabled:opacity-50"
                        title="Grant admin access"
                      >
                        {actionLoading === `admin-${user.userId}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Shield className="w-3 h-3" />
                        )}
                        Make Admin
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {sortedUsers.length === 0 && (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                  {searchQuery || filter !== 'all' ? 'No users match your filters' : 'No users found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Info Box */}
      <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-600">
        <strong>Publishing Restrictions:</strong>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li><strong>Restricted</strong> (default): User's public products are hidden from the gallery</li>
          <li><strong>Approved</strong>: User's public products are visible to everyone</li>
          <li>Admins can also hide individual products from the Products tab</li>
        </ul>
      </div>
    </div>
  );
}

export default UsersTab;
